import * as THREE from "three";
import type { PickupType } from "../level/types";

// Tiny floating gem the player rolls into. Per-type geometry + color so
// players can read pickup intent at a glance:
//   - score   : gold octahedron
//   - shield  : cyan icosahedron
//   - slowmo  : magenta torus
//   - magnet  : violet torus-knot (visually "twisted attractor")
//   - double  : gold tetrahedron (sharper / brighter than score orb so
//               the player tells them apart at speed)
//
// Each instance owns its own geometry + material (cheap; ~30 verts and
// one MeshStandardMaterial per orb, and the count never exceeds ~20
// across all active segments). Sharing across instances would force
// us to manage mesh.material color per-orb later when we want to flash
// on graze, which isn't worth the savings.

const ORB_RADIUS = 0.42;

export interface PickupVisualConfig {
  color: number;
  emissive: number;
}

const VISUAL_BY_TYPE: Record<PickupType, PickupVisualConfig> = {
  score: { color: 0xffd23f, emissive: 0xffaa00 },
  shield: { color: 0x6bd2ff, emissive: 0x148ad6 },
  slowmo: { color: 0xff5cb1, emissive: 0xb8197b },
  magnet: { color: 0xb265ff, emissive: 0x6a1fd6 },
  double: { color: 0xffe070, emissive: 0xff8a00 },
};

export function createPickupMesh(type: PickupType): THREE.Mesh {
  let geo: THREE.BufferGeometry;
  if (type === "score") {
    geo = new THREE.OctahedronGeometry(ORB_RADIUS, 0);
  } else if (type === "shield") {
    geo = new THREE.IcosahedronGeometry(ORB_RADIUS, 0);
  } else if (type === "slowmo") {
    geo = new THREE.TorusGeometry(ORB_RADIUS * 0.7, ORB_RADIUS * 0.22, 10, 18);
  } else if (type === "magnet") {
    // Torus-knot reads as "swirling field" -- low tube counts keep it
    // cheap (~96 verts) and silhouette-clear at speed.
    geo = new THREE.TorusKnotGeometry(
      ORB_RADIUS * 0.55,
      ORB_RADIUS * 0.18,
      48,
      6,
      2,
      3
    );
  } else {
    // Sharp tetrahedron at slightly larger radius for the 2x score
    // orb so it visually "shouts" at the player from distance.
    geo = new THREE.TetrahedronGeometry(ORB_RADIUS * 1.05, 0);
  }

  const visual = VISUAL_BY_TYPE[type];
  const mat = new THREE.MeshStandardMaterial({
    color: visual.color,
    emissive: visual.emissive,
    // Magnet + double get a hotter emissive so they read as "powerful"
    // even on dark themes. The render pipeline's bloom pass takes care
    // of the bloom halo without us tuning per-shape light rigs.
    emissiveIntensity: type === "magnet" || type === "double" ? 1.6 : 1.2,
    roughness: 0.25,
    metalness: 0.0,
  });

  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = true;
  mesh.name = `pickup.${type}`;
  return mesh;
}

export function disposePickupMesh(mesh: THREE.Mesh): void {
  mesh.geometry.dispose();
  const mat = mesh.material as THREE.Material | THREE.Material[];
  if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
  else mat.dispose();
}
